import React, { useCallback, useMemo, startTransition } from 'react';
import { AppTabularDto } from '../../../../components/reports/dtos/app-tabular.dto';
import {
  DataTableFlatComponent,
  DataTableFlatComponentProps,
} from '../../../../components/reports/generic-table/data-table-flat.component';

interface DataTableFlatWithHideRowsProps
  extends Omit<
    DataTableFlatComponentProps,
    | 'isShowOnlyFilteredMode'
    | 'hiddenRows'
    | 'selectedRowsForHiding'
    | 'onRowSelectionForHiding'
    | 'showOnlyFilteredRows'
    | 'onClearShowOnlyFiltered'
  > {
  // Hide rows functionality props
  hiddenRows?: Set<string>;
  selectedRowsForHiding?: string[];
  onRowSelectionForHiding?: (selectedRows: string[]) => void;
  showOnlyFilteredRows?: string[];
  onClearShowOnlyFiltered?: () => void;
  isShowOnlyFilteredMode?: boolean;
}

export const DataTableFlatWithHideRowsComponent =
  React.memo<DataTableFlatWithHideRowsProps>(
    ({
      data,
      hiddenRows = new Set(),
      selectedRowsForHiding = [],
      onRowSelectionForHiding,
      showOnlyFilteredRows = [],
      onClearShowOnlyFiltered,
      isShowOnlyFilteredMode = false,
      onRowSelectionChange,
      idField = 'id',
      ...otherProps
    }) => {
      const shouldShowRow = useCallback(
        (row: any) => {
          if (showOnlyFilteredRows.length > 0) {
            return (
              row.stableKey && showOnlyFilteredRows.includes(row.stableKey)
            );
          }

          const rowIdentifiers = [
            String(row[idField] || ''),
            String(row.id || ''),
            String(row.originalId || ''),
            String(row.stableKey || ''),
            String(row.name || ''),
          ].filter((id) => id && id !== '');

          return !rowIdentifiers.some((identifier) =>
            hiddenRows.has(identifier),
          );
        },
        [hiddenRows, showOnlyFilteredRows, idField],
      );

      const filteredData: AppTabularDto<any> | undefined = useMemo(() => {
        if (
          !data ||
          (hiddenRows.size === 0 && showOnlyFilteredRows.length === 0)
        ) {
          return data;
        }

        const filteredRows = data.rows
          .map((parentRow: any) => {
            if (showOnlyFilteredRows.length > 0) {
              const showParent = shouldShowRow(parentRow);
              const filteredChildren =
                parentRow.children?.filter((child: any) => {
                  return (
                    child.stableKey &&
                    showOnlyFilteredRows.includes(child.stableKey)
                  );
                }) || [];

              if (showParent || filteredChildren.length > 0) {
                return {
                  ...parentRow,
                  children: filteredChildren,
                };
              }
              return null;
            } else {
              const showParent = shouldShowRow(parentRow);
              if (!showParent) {
                return null;
              }
              const filteredChildren =
                parentRow.children?.filter(shouldShowRow) || [];
              return {
                ...parentRow,
                children: filteredChildren,
              };
            }
          })
          .filter(Boolean);

        return {
          ...data,
          rows: filteredRows,
        };
      }, [data, hiddenRows, showOnlyFilteredRows, shouldShowRow]);

      const handleRowSelectionChange = useCallback(
        (selectedRows: any[]) => {
          if (isShowOnlyFilteredMode) {
            if (onRowSelectionForHiding) {
              onRowSelectionForHiding([]);
            }
            if (onRowSelectionChange) {
              onRowSelectionChange([]);
            }
            return;
          }

          startTransition(() => {
            if (onRowSelectionChange) {
              onRowSelectionChange(selectedRows);
            }

            if (onRowSelectionForHiding && Array.isArray(selectedRows)) {
              const uniqueIds = new Set<string>();

              selectedRows.forEach((row) => {
                let rowId = null;

                if (row.stableKey) {
                  rowId = row.stableKey;
                } else if (row[idField]) {
                  rowId = row[idField];
                } else if (row.id) {
                  rowId = row.id;
                } else if (row.originalId) {
                  rowId = row.originalId;
                } else if (row.name) {
                  rowId = row.name;
                }

                if (rowId != null && rowId !== '') {
                  const stringId = String(rowId);
                  uniqueIds.add(stringId);
                }

                if (row.children && Array.isArray(row.children)) {
                  row.children.forEach((child: any) => {
                    if (child.isSelected || child.selected) {
                      let childId = null;

                      if (child.stableKey) {
                        childId = child.stableKey;
                      } else if (child[idField]) {
                        childId = child[idField];
                      } else if (child.id) {
                        childId = child.id;
                      } else if (child.originalId) {
                        childId = child.originalId;
                      } else if (child.name) {
                        childId = child.name;
                      }

                      if (childId != null && childId !== '') {
                        const stringChildId = String(childId);
                        uniqueIds.add(stringChildId);
                      }
                    }
                  });
                }
              });

              const finalIds = Array.from(uniqueIds);
              onRowSelectionForHiding(finalIds);
            }
          });
        },
        [
          isShowOnlyFilteredMode,
          onRowSelectionChange,
          onRowSelectionForHiding,
          idField,
        ],
      );

      return (
        <DataTableFlatComponent
          {...otherProps}
          data={filteredData}
          idField={idField}
          onRowSelectionChange={handleRowSelectionChange}
          enableRowSelection={
            otherProps.enableRowSelection && !isShowOnlyFilteredMode
          }
          enableMultiRowSelection={
            otherProps.enableMultiRowSelection && !isShowOnlyFilteredMode
          }
          suppressRowClickSelection={
            otherProps.suppressRowClickSelection || isShowOnlyFilteredMode
          }
        />
      );
    },
    (prevProps, nextProps) => {
      return (
        prevProps.data === nextProps.data &&
        prevProps.totalData === nextProps.totalData &&
        prevProps.hiddenRows === nextProps.hiddenRows &&
        prevProps.showOnlyFilteredRows === nextProps.showOnlyFilteredRows &&
        prevProps.isShowOnlyFilteredMode === nextProps.isShowOnlyFilteredMode &&
        prevProps.selectedRowsForHiding === nextProps.selectedRowsForHiding &&
        prevProps.enableRowSelection === nextProps.enableRowSelection &&
        prevProps.height === nextProps.height &&
        prevProps.className === nextProps.className
      );
    },
  );

DataTableFlatWithHideRowsComponent.displayName =
  'DataTableFlatWithHideRowsComponent';
